import type { ClintplayEvent, RageClickEventData } from '../types';

type EventCallback = (event: ClintplayEvent) => void;

const RAGE_THRESHOLD = 3;
const RAGE_WINDOW_MS = 1000;

interface ClickRecord {
  element: Element;
  timestamps: number[];
}

export class RageClickDetector {
  private clickHandler: ((e: MouseEvent) => void) | null = null;
  private recentClicks: ClickRecord[] = [];

  constructor(
    private sessionId: string,
    private emit: EventCallback
  ) {}

  start(): void {
    this.clickHandler = (e: MouseEvent) => {
      const target = e.target as Element;
      if (!target) return;
      const now = Date.now();
      let record = this.recentClicks.find((r) => r.element === target);
      if (!record) {
        record = { element: target, timestamps: [] };
        this.recentClicks.push(record);
      }
      record.timestamps.push(now);
      record.timestamps = record.timestamps.filter((t) => now - t < RAGE_WINDOW_MS);
      if (record.timestamps.length >= RAGE_THRESHOLD) {
        const data: RageClickEventData = {
          selector: this.getSelector(target),
          clickCount: record.timestamps.length,
          x: e.clientX,
          y: e.clientY,
        };
        this.emit({
          type: 'rage-click',
          timestamp: now,
          sessionId: this.sessionId,
          data,
        });
        record.timestamps = [];
      }
      this.recentClicks = this.recentClicks.filter((r) => r.timestamps.length > 0);
    };
    document.addEventListener('click', this.clickHandler, true);
  }

  stop(): void {
    if (this.clickHandler) {
      document.removeEventListener('click', this.clickHandler, true);
      this.clickHandler = null;
    }
    this.recentClicks = [];
  }

  private getSelector(el: Element): string {
    if (el.id) return `#${el.id}`;
    const testId = el.getAttribute('data-testid');
    if (testId) return `[data-testid="${testId}"]`;
    const tag = el.tagName.toLowerCase();
    const className = el.className
      ? `.${el.className.split(' ').filter(Boolean).join('.')}`
      : '';
    return `${tag}${className}`;
  }
}
